import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useState } from "react";
import Draggable from "react-draggable";
import { fetchTimeOffRequestTypes } from "../api/timeOffRequest";
import ConfirmButton from "../components/fundamentals/ConfirmButton";
import {
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  DAT_TIME_OFF_TYPE,
  DST_WARNING,
  FTO_TIME_OFF_TYPE,
  SELECT_HOURS_LIST,
  SELECT_MINUTES_LIST,
  TIME_OFF_PAGE_TYPE,
} from "../util/constants";
import { convert_Luxon_To_DateFns } from "../util/dateTimeFormatter.js";
import {
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE,
  EAD_EDU_SEAD_MISSING_REQUEST_TIME_MESSAGE,
  GENERIC_LOADING_MESSAGE,
  GENERIC_TRY_AGAIN_ERROR_MESSAGE,
} from "../util/messageConstants";
import {
  isEADorEDUorSEAD,
  TOR_SUBMISSION_CUTOFF_TIME_REMINDER,
  isLWOPTOWOPRequest,
  isLWOPTOWOPDayOfRequest,
} from "../util/timeOffRequestUtils";
import { getUnionContractGivenWorkgroup } from "../util/workgroupMapper.js";
import ConfirmationDialog from "./fundamentals/ConfirmationDialog.jsx";
import SlideUpTransition from "./fundamentals/SlideUpTransition";
import StarsDatePicker from "./fundamentals/StarsDatePicker";
import UnionContractCutoffTimeReminder from "./UnionContractCutoffTimeReminder";
import UnionContractHoldRequestReminder from "./UnionContractHoldRequestReminder";
import { Checkbox, FormControlLabel, FormLabel, TextField } from "@mui/material";

const useStyles = makeStyles((theme) => ({
  dialog: {
    width: "502px",
    margin: "auto",
    maxWidth: "100vw",
    "& .MuiBackdrop-root": {
      background: "transparent",
    },
  },
  dialogTitle: {
    cursor: "move",
  },
  dialogContent: {
    padding: "10px",
    maxWidth: "375px",
    margin: "auto",
  },
  selectInput: {
    maxWidth: "95vw",
    width: "354px",
  },
  dateRoot: {
    marginLeft: "-8px",
    width: "199px",
  },
  buttonWrapper: {
    margin: "auto",
    display: "flex",
    width: "inherit",
  },
  removeButton: {
    background: theme.palette.primary.swaRed,
    "&:hover": {
      backgroundColor: theme.palette.primary.swaRed,
    },
  },
  alertInfo: {
    marginTop: theme.spacing(1),
  },
  messageBorder: {
    width: "148px",
  },
  messageBorderWOP: {
    width: "230px",
  },
  warningFormat: {
    textAlign: "center",
    whiteSpace: "pre-line",
    float: "left",
    marginLeft: "15px",
  },
  datetimeInputGrid: {
    maxWidth: "100vw",
    height: "max-content",
  },
  dateInputBox: {
    display: "inline-block",
  },
  datetimeInputBox: {
    display: "inline-block",
  },
  datetimeInputBoxWOP: {
    display: "inline-block",
    width: "100%",
  },
  timeInputBox: {
    display: "inline-block",
  },
  timeInput: {
    width: "70px",
    maxWidth: "20vw",
  },
  timeInputDayOf: {
    width: "110px",
    maxWidth: "20vw",
  },
  timeColonBox: {
    display: "inline-block",
    fontSize: "20px",
    lineHeight: "0",
    top: "18px",
    position: "relative",
  },
}));

const AddTimeOffRequestDialog = ({
  isAdmin,
  isCreateTimeOff,
  employeeWorkgroup,
  employeeJobCode,
  isDialogOpen,
  closeAddTimeOff,
  handleChangeTimeOffTypes,
  selectedTimeOffType,
  handleOnSave,
  timeOffTypeError,
  handleShiftStartDateChange,
  handleShiftStartDateHoursChange,
  handleShiftStartDateMinChange,
  handleShiftEndDateChange,
  handleShiftEndDateHoursChange,
  handleShiftEndDateMinChange,
  shiftStartDate,
  shiftStartDateHours,
  shiftStartDateMin,
  shiftEndDate,
  shiftEndDateHours,
  shiftEndDateMin,
  station,
  shiftStartDateError,
  shiftStartTimeError,
  shiftEndDateError,
  shiftEndDateTimeError,
  handleOnRemove,
  isEdit,
  isAdminPage,
  isDesktopView,
  isDSTWarningOpen,
  asSoonAsPossibleChecked,
  handleAsSoonAsPossibleChecked,
  preferredWOPStartHour,
  preferredWOPStartMin,
  preferredWOPStartTimeError,
  handlePreferredWOPStartHourChange,
  handlePreferredWOPStartMinChange,
  preferredWOPEndHour,
  preferredWOPEndMin,
  preferredWOPEndTimeError,
  handlePreferredWOPEndHourChange,
  handlePreferredWOPEndMinChange,
  phoneNumber,
  handlePhoneNumberChange,
  handlePhoneNumberBlur,
  phoneNumberErrors,
}) => {
  const classes = useStyles();
  const [timeOffRequestTypeList, setTimeOffRequestTypeList] = useState([]);
  const [isCancelConfirmDialogOpen, setIsCancelConfirmDialogOpen] = useState(false);
  const TORSubmissionConfig = !!employeeWorkgroup
    ? TOR_SUBMISSION_CUTOFF_TIME_REMINDER[getUnionContractGivenWorkgroup(employeeWorkgroup)][selectedTimeOffType]
    : "";

  useEffect(() => {
    if (
      timeOffRequestTypeList === null ||
      timeOffRequestTypeList.length === 0 ||
      timeOffRequestTypeList[0] === GENERIC_TRY_AGAIN_ERROR_MESSAGE
    ) {
      fetchTimeOffRequestTypes(employeeJobCode, station)
        .then((res) => res.json())
        .then((result) => {
          if (!!result[employeeJobCode]) {
            setTimeOffRequestTypeList(result[employeeJobCode]?.sort());
          } else {
            setTimeOffRequestTypeList([GENERIC_TRY_AGAIN_ERROR_MESSAGE]);
          }
        })
        .catch((err) => {
          console.error("Error after Api Response", err);
          setTimeOffRequestTypeList([GENERIC_TRY_AGAIN_ERROR_MESSAGE]);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderHours = () => {
    return SELECT_HOURS_LIST.map((hour) => {
      return (
        <MenuItem key={hour} value={parseInt(hour)}>
          {hour}
        </MenuItem>
      );
    });
  };

  const renderMinutes = () => {
    return SELECT_MINUTES_LIST.map((minute) => {
      return (
        <MenuItem key={minute} value={parseInt(minute)}>
          {minute}
        </MenuItem>
      );
    });
  };

  return (
    <Draggable handle="#alert-dialog-slide-title">
      <Dialog
        className={classes.dialog}
        fullScreen={!isDesktopView}
        fullWidth
        maxWidth="md"
        open={isDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
        data-testid="add-tor-dialog"
      >
        <DialogTitle className={classes.dialogTitle} id="alert-dialog-slide-title">
          {isEdit ? "Edit Request" : "Add Request"}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <DialogContentText component={"div"} id="alert-dialog-slide-description">
            <Box mt={1}>
              <FormControl fullWidth variant="outlined" error={!!timeOffTypeError}>
                <InputLabel>Time Off Type</InputLabel>
                <Select
                  labelId="select-label"
                  id="simple-select"
                  value={!!selectedTimeOffType ? selectedTimeOffType : ""}
                  label="Time Off Type"
                  onChange={handleChangeTimeOffTypes}
                  className={classes.selectInput}
                >
                  {timeOffRequestTypeList.length > 0 ? (
                    timeOffRequestTypeList.map((item, index) => (
                      <MenuItem key={item} disabled={item === GENERIC_TRY_AGAIN_ERROR_MESSAGE} value={item}>
                        {item}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem key={GENERIC_LOADING_MESSAGE} disabled={true} value={GENERIC_LOADING_MESSAGE}>
                      {GENERIC_LOADING_MESSAGE}
                    </MenuItem>
                  )}
                </Select>
                {!!selectedTimeOffType && isEADorEDUorSEAD(selectedTimeOffType) ? (
                  <Alert severity="info" className={classes.alertInfo}>
                    {EAD_EDU_SEAD_MISSING_REQUEST_TIME_MESSAGE}
                  </Alert>
                ) : (
                  <FormHelperText>{timeOffTypeError}</FormHelperText>
                )}
              </FormControl>
            </Box>
          </DialogContentText>
          {!!selectedTimeOffType && (
            <>
              <Grid className={classes.datetimeInputGrid} item xs={12}>
                <Box className={classes.datetimeInputBox} m={1}>
                  <StarsDatePicker
                    label={isLWOPTOWOPRequest(selectedTimeOffType) ? "Shift Start Date/Time" : "Start Date/Time"}
                    date={shiftStartDate}
                    onChange={handleShiftStartDateChange}
                    dateErrors={shiftStartDateError}
                    minDate={
                      isLWOPTOWOPRequest(selectedTimeOffType)
                        ? convert_Luxon_To_DateFns(
                            !!TORSubmissionConfig &&
                              TORSubmissionConfig.getMinSubmissionShiftDate(station, isAdminPage).plus({
                                days: isAdminPage ? -1 : 0,
                              })
                          )
                        : convert_Luxon_To_DateFns(
                            !!TORSubmissionConfig && TORSubmissionConfig.getMinSubmissionShiftDate(station, isAdminPage)
                          )
                    }
                    maxDate={convert_Luxon_To_DateFns(
                      !!TORSubmissionConfig && TORSubmissionConfig.getMaxSubmissionShiftDate(station)
                    )}
                    className={classes.dateRoot}
                  />
                </Box>

                <Box className={classes.datetimeInputBox} style={{ marginRight: "0px" }} mt={1} mb={1} mr={1}>
                  <Grid container className={classes.dateInputBox}>
                    <Grid item xs={12} className={classes.messageBorder}>
                      <Box className={classes.timeInputBox}>
                        <FormControl variant="outlined">
                          <InputLabel id="offeredShiftStartHourSelectLabel" error={!!shiftStartTimeError}>
                            Hr
                          </InputLabel>
                          <Select
                            labelId="offeredShiftStartHourSelectLabel"
                            id="offeredShiftStartHourSelectElement"
                            className={classes.timeInput}
                            value={shiftStartDateHours !== undefined ? shiftStartDateHours : ""}
                            onChange={handleShiftStartDateHoursChange}
                            label="Hr"
                            error={!!shiftStartTimeError}
                          >
                            <MenuItem value="" disabled>
                              <em>Hour</em>
                            </MenuItem>
                            {renderHours()}
                          </Select>
                        </FormControl>
                      </Box>
                      <Box className={classes.timeColonBox}>:</Box>
                      <Box className={classes.timeInputBox}>
                        <FormControl variant="outlined">
                          <InputLabel id="offeredShiftStartMinuteSelectLabel" error={!!shiftStartTimeError}>
                            Min
                          </InputLabel>
                          <Select
                            labelId="offeredShiftStartMinuteSelectLabel"
                            id="offeredShiftStartMinuteSelectElement"
                            className={classes.timeInput}
                            value={shiftStartDateMin !== undefined ? shiftStartDateMin : ""}
                            onChange={handleShiftStartDateMinChange}
                            label="Min"
                            error={!!shiftStartTimeError}
                          >
                            <MenuItem value="" disabled>
                              <em>Minute</em>
                            </MenuItem>
                            {renderMinutes()}
                          </Select>
                        </FormControl>
                      </Box>
                      <FormHelperText className={classes.warningFormat} error>
                        {shiftStartTimeError}
                      </FormHelperText>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid className={classes.datetimeInputGrid} item xs={12}>
                <Box className={classes.datetimeInputBox} m={1}>
                  <StarsDatePicker
                    label={isLWOPTOWOPRequest(selectedTimeOffType) ? "Shift End Date/Time" : "End Date/Time"}
                    date={shiftEndDate}
                    onChange={handleShiftEndDateChange}
                    dateErrors={shiftEndDateError}
                    minDate={convert_Luxon_To_DateFns(
                      !!TORSubmissionConfig && TORSubmissionConfig.getMinSubmissionShiftDate(station, isAdminPage)
                    )}
                    maxDate={convert_Luxon_To_DateFns(
                      !!TORSubmissionConfig && TORSubmissionConfig.getMaxSubmissionShiftDate(station).plus({ days: 1 }) //support overnight shift
                    )}
                    className={classes.dateRoot}
                  />
                </Box>
                <Box className={classes.datetimeInputBox} style={{ marginRight: "0px" }} mt={1} mb={1} mr={1}>
                  <Grid container className={classes.dateInputBox}>
                    <Grid item xs={12} className={classes.messageBorder}>
                      <Box className={classes.timeInputBox}>
                        <FormControl variant="outlined">
                          <InputLabel id="offeredShiftEndHourSelectLabel" error={!!shiftEndDateTimeError}>
                            Hr
                          </InputLabel>
                          <Select
                            labelId="offeredShiftEndHourSelectLabel"
                            id="offeredShiftEndHourSelectElement"
                            className={classes.timeInput}
                            value={shiftEndDateHours !== undefined ? shiftEndDateHours : ""}
                            onChange={handleShiftEndDateHoursChange}
                            label="Hr"
                            error={!!shiftEndDateTimeError}
                          >
                            <MenuItem value="" disabled>
                              <em>Hour</em>
                            </MenuItem>
                            {renderHours()}
                          </Select>
                        </FormControl>
                      </Box>
                      <Box className={classes.timeColonBox}>:</Box>
                      <Box className={classes.timeInputBox}>
                        <FormControl variant="outlined">
                          <InputLabel id="offeredShiftEndMinuteSelectLabel" error={!!shiftEndDateTimeError}>
                            Min
                          </InputLabel>
                          <Select
                            labelId="offeredShiftEndMinuteSelectLabel"
                            id="offeredShiftEndMinuteSelectElement"
                            className={classes.timeInput}
                            value={shiftEndDateMin !== undefined ? shiftEndDateMin : ""}
                            onChange={handleShiftEndDateMinChange}
                            label="Min"
                            error={!!shiftEndDateTimeError}
                          >
                            <MenuItem value="" disabled>
                              <em>Minute</em>
                            </MenuItem>
                            {renderMinutes()}
                          </Select>
                        </FormControl>
                      </Box>
                      <FormHelperText className={classes.warningFormat} error>
                        {shiftEndDateTimeError}
                      </FormHelperText>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {isLWOPTOWOPRequest(selectedTimeOffType) && (
                <Grid className={classes.datetimeInputGrid} item xs={12}>
                  <Box className={classes.datetimeInputBox} m={1}>
                    <TextField
                      className={classes.dateRoot}
                      value={phoneNumber}
                      variant="outlined"
                      label="Phone #"
                      onChange={handlePhoneNumberChange}
                      onBlur={handlePhoneNumberBlur}
                      error={phoneNumberErrors !== ""}
                      helperText={phoneNumberErrors}
                    />
                  </Box>
                </Grid>
              )}
              {isLWOPTOWOPDayOfRequest(selectedTimeOffType) && (
                <Grid container xs={12}>
                  <FormLabel sx={{ color: "black", fontWeight: "bold" }}>
                    {isAdminPage ? "Approved" : "Preferred"} Leave Time
                  </FormLabel>
                  {!isAdminPage && (
                    <FormControl variant="outlined">
                    <FormControlLabel
                      checked={asSoonAsPossibleChecked}
                      onChange={handleAsSoonAsPossibleChecked}
                      control={<Checkbox />}
                      label="As Soon As Possible"
                    />
                  </FormControl>
                  )}
                  
                  <Grid className={classes.datetimeInputGrid} item xs={12}>
                    <Box className={classes.datetimeInputBoxWOP} style={{ marginRight: "0px" }} mt={1} mb={1} mr={1}>
                      <Grid container className={classes.dateInputBox}>
                        <Grid item xs={12} className={classes.messageBorderWOP}>
                          <Box className={classes.timeInputBox}>
                            <FormControl variant="outlined">
                              <InputLabel id="preferredWOPStartHourSelectLabel" error={!!preferredWOPStartTimeError}>
                                Start Hr
                              </InputLabel>
                              <Select
                                labelId="preferredWOPStartHourSelectLabel"
                                id="preferredWOPStartHourSelectElement"
                                className={classes.timeInputDayOf}
                                value={preferredWOPStartHour !== undefined ? preferredWOPStartHour : ""}
                                onChange={handlePreferredWOPStartHourChange}
                                label="Start Hr"
                                error={!!preferredWOPStartTimeError}
                                disabled={asSoonAsPossibleChecked}
                              >
                                <MenuItem value="" disabled>
                                  <em>Hour</em>
                                </MenuItem>
                                {renderHours()}
                              </Select>
                            </FormControl>
                          </Box>
                          <Box className={classes.timeColonBox}>:</Box>
                          <Box className={classes.timeInputBox}>
                            <FormControl variant="outlined">
                              <InputLabel id="preferredWOPStartMinSelectLabel" error={!!preferredWOPStartTimeError}>
                                Start Min
                              </InputLabel>
                              <Select
                                labelId="preferredWOPStartMinSelectLabel"
                                id="preferredWOPStartMinSelectElement"
                                className={classes.timeInputDayOf}
                                value={preferredWOPStartMin !== undefined ? preferredWOPStartMin : ""}
                                onChange={handlePreferredWOPStartMinChange}
                                label="Start Min"
                                error={!!preferredWOPStartTimeError}
                                disabled={asSoonAsPossibleChecked}
                              >
                                <MenuItem value="" disabled>
                                  <em>Minute</em>
                                </MenuItem>
                                {renderMinutes()}
                              </Select>
                            </FormControl>
                          </Box>
                          <FormHelperText className={classes.warningFormat} error>
                            {preferredWOPStartTimeError}
                          </FormHelperText>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid className={classes.datetimeInputGrid} item xs={12}>
                    <Box className={classes.datetimeInputBoxWOP} style={{ marginRight: "0px" }} mt={1} mb={1} mr={1}>
                      <Grid container className={classes.dateInputBox}>
                        <Grid item xs={12} className={classes.messageBorderWOP}>
                          <Box className={classes.timeInputBox}>
                            <FormControl variant="outlined">
                              <InputLabel id="preferredWOPEndHourSelectLabel" error={!!preferredWOPEndTimeError}>
                                End Hr
                              </InputLabel>
                              <Select
                                labelId="preferredWOPEndHourSelectLabel"
                                id="preferredWOPEndHourSelectElement"
                                className={classes.timeInputDayOf}
                                value={preferredWOPEndHour !== undefined ? preferredWOPEndHour : ""}
                                onChange={handlePreferredWOPEndHourChange}
                                label="End Hr"
                                disabled={asSoonAsPossibleChecked}
                                error={!!preferredWOPEndTimeError}
                              >
                                <MenuItem value="" disabled>
                                  <em>Hour</em>
                                </MenuItem>
                                {renderHours()}
                              </Select>
                            </FormControl>
                          </Box>
                          <Box className={classes.timeColonBox}>:</Box>
                          <Box className={classes.timeInputBox}>
                            <FormControl variant="outlined">
                              <InputLabel id="preferredWOPEndMinuteSelectLabel" error={!!preferredWOPEndTimeError}>
                                End Min
                              </InputLabel>
                              <Select
                                labelId="preferredWOPEndMinuteSelectLabel"
                                id="preferredWOPEndMinuteSelectElement"
                                className={classes.timeInputDayOf}
                                value={preferredWOPEndMin !== undefined ? preferredWOPEndMin : ""}
                                onChange={handlePreferredWOPEndMinChange}
                                label="End Min"
                                disabled={asSoonAsPossibleChecked}
                                error={!!preferredWOPEndTimeError}
                              >
                                <MenuItem value="" disabled>
                                  <em>Minute</em>
                                </MenuItem>
                                {renderMinutes()}
                              </Select>
                            </FormControl>
                          </Box>
                          <FormHelperText className={classes.warningFormat} error>
                            {preferredWOPEndTimeError}
                          </FormHelperText>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              )}

              {isDSTWarningOpen && (
                <Grid container xs={12} sx={{ justifyContent: "center" }}>
                  <Alert severity="warning" sx={{ maxWidth: "360px", textAlign: "start", marginBottom: "10px" }}>
                    {DST_WARNING}
                  </Alert>
                </Grid>
              )}

              {!isCreateTimeOff && (
                <UnionContractCutoffTimeReminder pageType={TIME_OFF_PAGE_TYPE} timeOffType={selectedTimeOffType} />
              )}
              {!isCreateTimeOff &&
                (selectedTimeOffType === DAT_TIME_OFF_TYPE || selectedTimeOffType === FTO_TIME_OFF_TYPE) && (
                  <Grid sx={{ marginTop: "10px" }}>
                    <UnionContractHoldRequestReminder pageType={TIME_OFF_PAGE_TYPE} timeOffType={selectedTimeOffType} />
                  </Grid>
                )}
            </>
          )}
        </DialogContent>
        <DialogActions className={classes.buttonWrapper} style={{ justifyContent: isEdit ? "space-between" : "" }}>
          {isEdit && (
            <div>
              <ConfirmButton
                className={classes.removeButton}
                onClick={() => handleOnRemove()}
                variant="contained"
                color="primary"
              >
                REMOVE
              </ConfirmButton>
            </div>
          )}

          <div>
            <ConfirmButton onClick={() => setIsCancelConfirmDialogOpen(true)} variant="outlined" color="primary">
              CANCEL
            </ConfirmButton>
            <ConfirmButton onClick={() => handleOnSave()} variant="contained" color="primary">
              SAVE
            </ConfirmButton>
          </div>
        </DialogActions>
        {isCancelConfirmDialogOpen && (
          <ConfirmationDialog
            message={
              isEdit
                ? ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS)
                : ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA)
            }
            isDialogOpen={isCancelConfirmDialogOpen}
            handleNoButtonClicked={() => setIsCancelConfirmDialogOpen(false)}
            handleYesButtonClicked={() => closeAddTimeOff()}
          />
        )}
      </Dialog>
    </Draggable>
  );
};

export default AddTimeOffRequestDialog;
